import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers'
import {
    CommunityOfficeHours,
    CommunityProfile,
    CommunityWeekday,
    WebsiteExtractJob,
} from '../models/Company/types'

export const COMMUNITY_WEEKDAYS: CommunityWeekday[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]

export function emptyOfficeHours(): CommunityOfficeHours {
    return COMMUNITY_WEEKDAYS.reduce((hours, day) => {
        hours[day] = { closed: false, start: '', end: '' }
        return hours
    }, {} as CommunityOfficeHours)
}

export function officeHoursAreSet(
    hours?: CommunityOfficeHours | null,
): boolean {
    if (!hours) return false
    return COMMUNITY_WEEKDAYS.some(
        (day) => !!hours[day]?.start && !!hours[day]?.end,
    )
}

export function communityProfileQueryKey(apartmentId: number) {
    return ['community-profile', apartmentId] as const
}

function hydrateCommunityProfile(
    raw: Partial<CommunityProfile> | undefined,
): CommunityProfile {
    const base = emptyCommunityProfile()
    return {
        ...base,
        ...raw,
        pet_defaults: {
            ...base.pet_defaults,
            ...(raw?.pet_defaults || {}),
        },
        office_hours: {
            ...base.office_hours,
            ...(raw?.office_hours || {}),
        },
    }
}

export function emptyCommunityProfile(): CommunityProfile {
    return {
        website_url: '',
        website_filled_at: null,
        website_fill_url: '',
        profile_picture: '',
        logo_url: '',
        amenities: '',
        parking: '',
        laundry: '',
        utilities_included: '',
        income_requirements: '',
        security_deposit: '',
        pet_notes: '',
        insurance_notes: '',
        furnished: 'unknown',
        lease_style: 'unknown',
        roommate_matching: null,
        students_required: null,
        extra_fees: [],
        faqs: [],
        pet_defaults: {
            allowed_animal_types: [],
            max_pets_per_unit: '',
            notes: '',
        },
        office_hours: emptyOfficeHours(),
    }
}

export function mergeCommunityProposal(
    current: CommunityProfile,
    proposal: Partial<CommunityProfile>,
): CommunityProfile {
    const next: CommunityProfile = {
        ...current,
        amenities: current.amenities || proposal.amenities || '',
        parking: current.parking || proposal.parking || '',
        laundry: current.laundry || proposal.laundry || '',
        utilities_included:
            current.utilities_included || proposal.utilities_included || '',
        income_requirements:
            current.income_requirements || proposal.income_requirements || '',
        security_deposit:
            current.security_deposit || proposal.security_deposit || '',
        pet_notes: current.pet_notes || proposal.pet_notes || '',
        insurance_notes:
            current.insurance_notes || proposal.insurance_notes || '',
        furnished:
            current.furnished !== 'unknown'
                ? current.furnished
                : proposal.furnished || 'unknown',
        lease_style:
            current.lease_style !== 'unknown'
                ? current.lease_style
                : proposal.lease_style || 'unknown',
        roommate_matching:
            current.roommate_matching ?? proposal.roommate_matching ?? null,
        students_required:
            current.students_required ?? proposal.students_required ?? null,
        extra_fees: mergeFees(current.extra_fees, proposal.extra_fees || []),
        faqs: mergeFaqs(current.faqs, proposal.faqs || []),
        pet_defaults: {
            allowed_animal_types:
                current.pet_defaults.allowed_animal_types.length > 0
                    ? current.pet_defaults.allowed_animal_types
                    : proposal.pet_defaults?.allowed_animal_types || [],
            max_pets_per_unit:
                current.pet_defaults.max_pets_per_unit ||
                proposal.pet_defaults?.max_pets_per_unit ||
                '',
            notes:
                current.pet_defaults.notes ||
                proposal.pet_defaults?.notes ||
                '',
        },
        office_hours: officeHoursAreSet(current.office_hours)
            ? current.office_hours
            : officeHoursAreSet(proposal.office_hours)
            ? {
                  ...emptyOfficeHours(),
                  ...proposal.office_hours,
              }
            : current.office_hours,
        logo_url: proposal.logo_url || current.logo_url || '',
        profile_picture:
            proposal.profile_picture || current.profile_picture || '',
    }
    return next
}

function mergeFees(
    current: CommunityProfile['extra_fees'],
    incoming: CommunityProfile['extra_fees'],
) {
    const names = new Set(current.map((fee) => fee.name.toLowerCase()))
    return [
        ...current,
        ...incoming.filter(
            (fee) => fee.name && !names.has(fee.name.toLowerCase()),
        ),
    ]
}

function mergeFaqs(
    current: CommunityProfile['faqs'],
    incoming: CommunityProfile['faqs'],
) {
    const questions = new Set(
        current.map((faq) => faq.question.trim().toLowerCase()),
    )
    return [
        ...current,
        ...incoming
            .filter((faq) => faq.question && faq.answer)
            .filter((faq) => !questions.has(faq.question.trim().toLowerCase()))
            .map((faq) => ({
                question: faq.question,
                answer: faq.answer,
                enabled: faq.enabled !== false,
                source_url: faq.source_url || '',
            })),
    ]
}

export function communityProfileEditSignature(
    profile: CommunityProfile,
): string {
    return JSON.stringify({
        amenities: profile.amenities,
        parking: profile.parking,
        laundry: profile.laundry,
        utilities_included: profile.utilities_included,
        income_requirements: profile.income_requirements,
        security_deposit: profile.security_deposit,
        pet_notes: profile.pet_notes,
        insurance_notes: profile.insurance_notes,
        furnished: profile.furnished,
        lease_style: profile.lease_style,
        roommate_matching: profile.roommate_matching,
        students_required: profile.students_required,
        extra_fees: profile.extra_fees,
        faqs: profile.faqs,
        pet_defaults: profile.pet_defaults,
        office_hours: profile.office_hours,
        website_url: profile.website_url || '',
    })
}

export function isCommunityProfileDirty(
    draft: CommunityProfile,
    saved: CommunityProfile | undefined,
): boolean {
    if (!saved)
        return (
            communityProfileEditSignature(draft) !==
            communityProfileEditSignature(emptyCommunityProfile())
        )
    return (
        communityProfileEditSignature(draft) !==
        communityProfileEditSignature(saved)
    )
}

export function useCommunityProfile(apartmentId: number | undefined) {
    const queryClient = useQueryClient()
    const [jobId, setJobId] = useState<number | null>(null)

    const query = useQuery<CommunityProfile>({
        queryKey: communityProfileQueryKey(apartmentId ?? 0),
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/company/apartment/${apartmentId}/community-profile/`,
            )
            return hydrateCommunityProfile(res.data)
        },
        enabled: apartmentId != null,
    })

    const jobQuery = useQuery<WebsiteExtractJob>({
        queryKey: ['website-extract', apartmentId, jobId],
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/company/apartment/${apartmentId}/website-extract/${jobId}/`,
            )
            return res.data
        },
        enabled: apartmentId != null && jobId != null,
        refetchInterval: (data) => {
            if (
                !data ||
                data.status === 'queued' ||
                data.status === 'running'
            ) {
                return 2000
            }
            return false
        },
    })

    useEffect(() => {
        if (jobQuery.data?.status === 'failed') {
            toast.error(jobQuery.data.error || 'Could not read the website')
        }
    }, [jobQuery.data?.status, jobQuery.data?.error])

    const saveMutation = useMutation({
        mutationFn: async (payload: CommunityProfile) => {
            const res = await axiosInstance.patch(
                `/company/apartment/${apartmentId}/community-profile/`,
                payload,
            )
            return hydrateCommunityProfile(res.data)
        },
        onSuccess: (data) => {
            if (apartmentId != null) {
                queryClient.setQueryData(
                    communityProfileQueryKey(apartmentId),
                    data,
                )
                queryClient.invalidateQueries({ queryKey: ['voice-agent'] })
            }
            toast.success('Community profile saved')
        },
        onError: (err: { response?: { data?: { error?: string } } }) => {
            toast.error(
                err.response?.data?.error ?? 'Failed to save community profile',
            )
        },
    })

    const extractMutation = useMutation({
        mutationFn: async (url?: string) => {
            const res = await axiosInstance.post(
                `/company/apartment/${apartmentId}/website-extract/`,
                url ? { url } : {},
            )
            return res.data as WebsiteExtractJob
        },
        onSuccess: (job) => {
            setJobId(job.id)
            toast.info('Reading the property website…')
        },
        onError: (err: { response?: { data?: { error?: string } } }) => {
            toast.error(
                err.response?.data?.error ?? 'Could not start website extract',
            )
        },
    })

    const applyLogoMutation = useMutation({
        mutationFn: async (url: string) => {
            const res = await axiosInstance.post(
                `/company/apartment/${apartmentId}/apply-website-logo/`,
                { url },
            )
            return hydrateCommunityProfile(res.data)
        },
        onSuccess: (data) => {
            if (apartmentId != null) {
                queryClient.setQueryData(
                    communityProfileQueryKey(apartmentId),
                    data,
                )
                queryClient.invalidateQueries({ queryKey: ['voice-agent'] })
            }
            toast.success('Property logo updated')
        },
        onError: (err: { response?: { data?: { error?: string } } }) => {
            toast.error(
                err.response?.data?.error ?? 'Could not save the website logo',
            )
        },
    })

    const applyExtractMutation = useMutation({
        mutationFn: async ({
            jobId,
            profile,
            applyLogo,
            logoUrl,
        }: {
            jobId: number
            profile: CommunityProfile
            applyLogo?: boolean
            logoUrl?: string
        }) => {
            const res = await axiosInstance.post(
                `/company/apartment/${apartmentId}/website-extract/${jobId}/apply/`,
                {
                    ...profile,
                    apply_logo: !!applyLogo,
                    logo_url: logoUrl || '',
                },
            )
            return hydrateCommunityProfile(res.data)
        },
        onSuccess: (data) => {
            if (apartmentId != null) {
                queryClient.setQueryData(
                    communityProfileQueryKey(apartmentId),
                    data,
                )
                queryClient.invalidateQueries({ queryKey: ['voice-agent'] })
            }
            toast.success('Website details saved')
        },
        onError: (err: { response?: { data?: { error?: string } } }) => {
            toast.error(
                err.response?.data?.error ?? 'Could not save website details',
            )
        },
    })

    return {
        profile: query.data,
        isLoading: query.isLoading,
        isSaving: saveMutation.isLoading,
        saveProfile: saveMutation.mutateAsync,
        startExtract: extractMutation.mutateAsync,
        isExtracting:
            extractMutation.isLoading ||
            jobQuery.data?.status === 'queued' ||
            jobQuery.data?.status === 'running',
        extractJob: jobQuery.data,
        applyExtract: applyExtractMutation.mutateAsync,
        isApplyingExtract: applyExtractMutation.isLoading,
        applyLogo: applyLogoMutation.mutateAsync,
        isApplyingLogo: applyLogoMutation.isLoading,
    }
}
